/**
 * Selection-key encoding for the configurator store.
 *
 * Persisted selections live under `Record<string, number[]>` keyed by where
 * the answer was given:
 *   - `${questionId}`               — answered for the whole property
 *   - `${questionId}:f${floorId}`   — answered for one storey
 *   - `${questionId}:${roomId}`     — answered for one room
 *
 * The two older forms are unchanged, so a blob written before storeys entered
 * the tree still reads. Every mutation, aggregation and orphan-cleanup goes
 * through these helpers, so the format stays one invariant — the same
 * encoding carries the configurator's navigation keys, which live in a
 * separate state slice and are never conflated with selections.
 */

import {
  parseScopeKey,
  scopeChain,
  scopeKey,
  type SelectionScope,
} from './selection-scope'

export type SelectionKey = string

export function encodeSelectionKey(
  questionId: number,
  scope: SelectionScope,
): SelectionKey {
  const place = scopeKey(scope)
  return place ? `${questionId}:${place}` : String(questionId)
}

/**
 * Parses a key back into the question and the place it was answered at, or
 * null when the key is malformed — a caller filters those out of a blob that
 * has been corrupted, including the phantom `questionId=0` an empty segment
 * would otherwise pass as an integer.
 */
export function decodeSelectionKey(key: SelectionKey): {
  questionId: number
  scope: SelectionScope
} | null {
  const parts = key.split(':')
  if (parts.length > 2) return null

  const questionId = Number(parts[0])
  if (!Number.isInteger(questionId) || questionId <= 0) return null

  // A key with a segment that names no place is malformed, not a key for the
  // object: the object is written without one, and `10:` would be stored
  // beside `10` where nothing ever reads it again.
  const place = parts[1]
  if (place === '') return null

  const scope = parseScopeKey(place ?? '')
  return scope ? { questionId, scope } : null
}

/**
 * The key one step of the configurator walk is held by — the group anchoring
 * it and the place it stands at, in the shape a selection key has.
 *
 * Aliases rather than repeats: two encoders would be two chances to disagree.
 */
export const encodeStepKey = encodeSelectionKey

/**
 * The answer that counts at a place: the nearest one given, walking up.
 *
 * A place that may depart from what a group above it answered starts out
 * showing that answer, and only records one of its own once the customer picks
 * there — which is what makes "reset" mean "take the answer from above again".
 *
 * Generic in what a stored value is, because the runtime holds picks as a
 * `Set` for membership and the persisted blob holds them as an array: the
 * inheritance rule is the same for both, and it is the one rule that must not
 * exist twice.
 */
export function answerAt<V>(
  selections: Record<string, V>,
  questionId: number,
  scope: SelectionScope,
): { value: V; scope: SelectionScope } | null {
  for (const step of scopeChain(scope)) {
    const found = selections[encodeSelectionKey(questionId, step)]
    if (found !== undefined) return { value: found, scope: step }
  }
  return null
}
